/**
 * Adapter: converts parsed API partie data into a GameHistory
 * consumable by the existing ReplayMode component.
 */

import type { CardNumber } from '../types/game';
import { Contract } from '../types/game';
import { sortHand } from './cards';
import type { GameHistory, ReplayStep, TrickHistory, CardPlay } from '../game/gameHistory';
import type { Player } from '../game/gameActions';

export interface ParsedPartie {
  id: number;
  idtournoi: number;
  numdonne: number;
  playerCount?: number;
  joueurs: { position: number; pseudo: string; is_preneur: boolean }[];
  preneur: number;
  contrat: number;
  contrat_label: string;
  chien: number[];
  mains: Record<string, number[]>;
  plis: number[][];
  entames: number[];
  scoreduplicate?: number;
}

function cardSuit(card: CardNumber): number {
  if (card === 78) return -1;
  if (card >= 57 && card <= 77) return 4;
  return Math.floor((card - 1) / 14);
}

function cardValue(card: CardNumber): number {
  if (card === 78) return 0;
  if (card >= 57 && card <= 77) return card - 56;
  return ((card - 1) % 14) + 1;
}

function determineWinner(cards: CardPlay[]): number {
  if (cards.length === 0) return 0;
  const leadPlay = cards.find(play => play.card !== 78) ?? cards[0];
  const leadSuit = cardSuit(leadPlay.card);
  let winner = leadPlay;
  let highestTrump = leadSuit === 4 ? cardValue(leadPlay.card) : -1;
  let highestLead = leadSuit !== 4 ? cardValue(leadPlay.card) : -1;

  for (const play of cards) {
    if (play.card === 78) continue;
    const suit = cardSuit(play.card);
    const value = cardValue(play.card);
    if (suit === 4 && value > highestTrump) {
      highestTrump = value;
      winner = play;
      continue;
    }
    if (highestTrump < 0 && suit === leadSuit && value > highestLead) {
      highestLead = value;
      winner = play;
    }
  }

  return winner.player;
}

/** Enchères reconstruites (ordre à partir du donneur) — les enchères ne sont pas stockées en base CIA. */
export function buildSyntheticReplayBids(
  dealer: number,
  taker: number,
  contract: Contract | number,
  playerCount = 4,
): Array<{ player: number; bid: number; timestamp: number }> {
  const bids: Array<{ player: number; bid: number; timestamp: number }> = [];
  for (let i = 0; i < playerCount; i++) {
    const p = (dealer + 1 + i) % playerCount;
    bids.push({
      player: p,
      bid: p === taker ? Number(contract) : Contract.Pass,
      timestamp: 0,
    });
  }
  return bids;
}

/**
 * Build a GameHistory from parsed API partie data.
 * The backend already reconstructed mains/plis/entames from raw DB fields.
 */
export function buildGameHistoryFromPartie(partie: ParsedPartie): GameHistory {
  const playerCount = partie.playerCount || Math.min(partie.joueurs.length, 4) || 4;
  const posLabels = ['sud', 'est', 'nord', 'ouest'];

  const players: Player[] = Array.from({ length: playerCount }, (_, position) => {
    const player = partie.joueurs.find(j => j.position === position) ?? partie.joueurs[position];
    return {
      name: player?.pseudo?.replace(/¶/g, ' ') ?? `Joueur ${position + 1}`,
      position,
      isConnected: true,
    };
  });

  const contract = (Number(partie.contrat) as Contract) || Contract.Petite;
  const taker = Number(partie.preneur);
  const firstLeader = partie.entames.length > 0 ? (partie.entames[0] ?? 0) : 0;
  const dealer = (firstLeader - 1 + playerCount) % playerCount;
  const bids = buildSyntheticReplayBids(dealer, taker, contract, playerCount);

  // Initial hands: for the taker in petite/garde, we need the hand BEFORE chien
  const initialHands = new Map<number, CardNumber[]>();
  for (let i = 0; i < playerCount; i++) {
    const label = posLabels[i];
    const hand = [...(partie.mains[label] || [])];
    initialHands.set(i, sortHand(hand as CardNumber[]));
  }

  const chien = partie.chien as CardNumber[];
  const ecartCards = (partie.mains['ecart'] || []) as CardNumber[];

  // For petite/garde, the taker's initial hand (before chien) = played cards + ecart - chien
  // But the reconstructed mains already contain the cards the player played,
  // which includes chien cards they kept. We need the pre-chien hand.
  if (contract <= Contract.Garde && taker !== undefined) {
    const takerHand = initialHands.get(taker)!;
    // Add ecart cards (they were part of the taker's hand after chien)
    takerHand.push(...ecartCards);
    // Remove chien cards (they were added to hand from the chien, not original)
    for (const c of chien) {
      const idx = takerHand.indexOf(c);
      if (idx !== -1) takerHand.splice(idx, 1);
    }
    initialHands.set(taker, sortHand(takerHand));
  }

  // Build trick histories
  const tricks: TrickHistory[] = [];
  for (let t = 0; t < partie.plis.length; t++) {
    const pliCards = partie.plis[t];
    let leader = partie.entames[t] ?? 0;
    if (leader >= playerCount) leader = 0;

    const cards: CardPlay[] = [];
    let pos = leader;
    for (let c = 0; c < pliCards.length; c++) {
      cards.push({
        player: pos,
        card: pliCards[c] as CardNumber,
        timestamp: t * 5000 + c * 1000, // synthetic timestamps for autoplay
      });
      pos = (pos + 1) % playerCount;
    }

    const winner = determineWinner(cards);

    tricks.push({
      trickNumber: t + 1,
      leader,
      cards,
      winner,
    });
  }

  // Build steps
  const steps: ReplayStep[] = [];
  steps.push({ type: 'distribution', label: 'DISTRIB' });

  if (bids.length > 0) {
    steps.push({ type: 'distribution', label: 'ENCH' });
  }

  if (chien.length > 0 && contract <= Contract.Garde) {
    steps.push({ type: 'chien', label: 'CHIEN' });
  }
  if ((ecartCards.length > 0) && contract <= Contract.Garde) {
    steps.push({ type: 'ecart', label: 'ECART' });
  }

  for (const trick of tricks) {
    steps.push({
      type: 'trick',
      trickNumber: trick.trickNumber,
      label: `${trick.trickNumber}`,
    });
  }

  return {
    players,
    playerCount,
    dealer,
    taker,
    contract,
    myPosition: 0,
    initialHands,
    chien,
    ecart: ecartCards,
    bids,
    tricks,
    poignees: [],
    score: undefined,
    steps,
  };
}
